/* eslint-disable camelcase */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { lensPath, set } from 'ramda';

import Form from './MasterForm';
import Title from './Title';
import { fillSpace, breakpoints } from '../../constants/styles';
import { getGoalNames, iOSSafari } from '../../constants/utils';

import { HEADER_HEIGHT, FlowHeader } from '../DebtConsolidationFlow/styles';

const SCROLL_OFFSET = 350;

const StyledRetirementFlow = styled.div`
  display: flex;
  flex-direction row;
  ${fillSpace}
`;

const FlowContent = styled.div`
  display: flex;
  align-items: center;
  height: calc(100vh - 50px);
  margin-top: ${HEADER_HEIGHT}px;
  padding: 0 20px;
  background-color: white;

  ${breakpoints.tablet} {
    width: 70%;
    margin-left: 15%;
    margin-right: 15%;
  }

  ${breakpoints.tabletLandscape} {
    width: 60%;
    margin-left: 20%;
    margin-right: 20%;
  }
`;

class RetirementFlow extends Component {
  state = {
    isFinished: false,
    questionnaire: [],
    focusedForm: 0,
    focusedQuestion: 0,
    questionsAnswered: [],
    inputList: [],
    form: [],
  };

  componentDidMount = () => {
    this.initializeForm(this.props);
    this.setQuestionAsFocused();
  };

  componentWillUnmount() {}

  setFormAsFocused = (goalIndex = 0) => {
    this.setState(state => ({
      ...set(lensPath(['questionnaire', goalIndex, 'isFocused']), true, state),
      focusedForm: goalIndex,
    }));
  };

  setFormAsAnswered = (goalIndex = 0) => {
    this.setState(state => ({
      ...set(lensPath(['questionnaire', goalIndex, 'isAnswered']), true, state),
    }));
  };

  setQuestionAsFocused = (goalIndex = 0, questionIndex = 0) => {
    this.setFormAsFocused(goalIndex);
    this.setState(state => ({
      ...set(
        lensPath(['questionnaire', goalIndex, 'questions', questionIndex, 'isFocused']),
        true,
        state,
      ),
      focusedForm: goalIndex,
      focusedQuestion: questionIndex,
    }));
  };

  setAllAsBlur = cb => {
    const { questionnaire } = this.state;
    this.setState(
      state => ({
        ...state,
        questionnaire: questionnaire.map(goal => ({
          ...goal,
          isFocused: false,
          questions: goal.questions.map(question => ({
            ...question,
            isFocused: false,
          })),
        })),
      }),
      cb,
    );
  };

  setFormAnswers = (values, goalName, goalIndex, cb) => {
    const { formatValues } = this.props;
    this.setState(
      state => ({
        ...set(lensPath(['form', goalIndex]), formatValues({ ...values }), state),
      }),
      () => {
        cb();
      },
    );
  };

  initializeForm = ({ questionnaire }, cb) => {
    this.setState(
      {
        questionnaire,
        goalNames: getGoalNames(questionnaire),
      },
      () => {
        this.updateInputList(cb);
      },
    );
  };

  updateInputList = cb => {
    const inputsFound = document.querySelectorAll('.question');
    const inputList = [];
    inputsFound.forEach(input => {
      inputList.push(input.id);
    });
    this.setState({ inputList }, cb);
  };

  injectNewQuestions = (newQuestions = [], goalIndex, questionIndex, ditchedQuestions, cb) => {
    const { questionnaire } = this.state;
    const newQuestionnaire = [...questionnaire];
    newQuestionnaire[goalIndex].questions.splice(questionIndex + 1, 0, ...[...newQuestions]);
    newQuestionnaire[goalIndex].questions[questionIndex].lastSaved = ditchedQuestions;
    this.initializeForm({ questionnaire: newQuestionnaire }, cb);
  };

  removeNewQuestions = (goalIndex, questionIndex, _, cb) => {
    const { questionnaire } = this.state;
    const newQuestionnaire = [...questionnaire];

    if (newQuestionnaire[goalIndex]) {
      for (let i = questionIndex + 1; i < newQuestionnaire[goalIndex].questions.length; ) {
        if (!newQuestionnaire[goalIndex].questions[i].base) {
          newQuestionnaire[goalIndex].questions.splice(i, 1);
        } else {
          break;
        }
      }
    }

    this.initializeForm(
      {
        questionnaire: newQuestionnaire,
      },
      cb,
    );
  };

  handleQuestionFocus = (ev, index, goalId) => {
    const { questionnaire } = this.state;
    const element = ev.target;
    const currentGoalIndex = questionnaire.findIndex(goal => goal.value === goalId);
    this.setAllAsBlur(() => {
      this.setQuestionAsFocused(currentGoalIndex, index);
      /** We need to do it with this method (getBoundingClientRect) so we can preserve the
       * right offset even with relative positioned elements.
       */
      const offsetTop = element.getBoundingClientRect().top + window.scrollY;
      if (!iOSSafari) {
        window.scrollTo({ top: offsetTop - SCROLL_OFFSET });
      }
    });
  };

  handleQuestionChange = ev => {
    this.setState({ isFinished: false });
    const inputId = ev.target.id;
    this.setState({ currentInputId: inputId });
  };

  handleQuestionBlur = (ev, { values = [], goalAnswered, goalIndex, goalName }) => {
    const arrValues = Object.keys(values).map(key => values[key]);
    this.setState(state => ({
      ...set(lensPath(['questionsAnswered', goalIndex]), arrValues.length, state),
    }));
    this.setFormAnswers(values, goalName, goalIndex, () => {
      if (goalAnswered) {
        this.setFormAsAnswered(goalIndex);
      }
    });
  };

  handleFormAnswered = (data, cb) => {
    const { values = [], goalAnswered, goalIndex, goalName } = data;
    const arrValues = Object.keys(values).map(key => values[key]);
    this.setState(state => ({
      ...set(lensPath(['questionsAnswered', goalIndex]), arrValues.length, state),
    }));
    this.setFormAnswers(values, goalName, goalIndex, () => {
      if (goalAnswered) {
        this.setFormAsAnswered(goalIndex);
      }
      cb();
    });
  };

  handleStepSubmit = () => {
    const { form } = this.state;
    const { onStepSubmit } = this.props;
    onStepSubmit(form);
  };

  goToNextInput = questionId => {
    const { inputList } = this.state;
    const currentInputIndex = inputList.findIndex(id => id === questionId);
    const nextInputName = inputList[currentInputIndex + 1];

    const nextInput = document.querySelector(`#${nextInputName}`);

    if (nextInput) {
      nextInput.focus();
      if (!iOSSafari) {
        window.scrollTo({ top: nextInput.offsetTop - SCROLL_OFFSET });
      }
    } else {
      this.setState({ isFinished: true });
      this.handleStepSubmit(questionId);
    }
  };

  render() {
    const { questionnaire, isFinished } = this.state;
    const { flowTitle } = this.props;
    return (
      <StyledRetirementFlow>
        <FlowHeader>
          <Title
            level={5}
            color="white"
            styles={css`
              margin-bottom: 0px;
            `}
            medium
          >
            {flowTitle}
          </Title>
        </FlowHeader>
        <FlowContent>
          <Form
            questionnaire={questionnaire}
            onQuestionFocus={this.handleQuestionFocus}
            onQuestionChange={this.handleQuestionChange}
            onQuestionBlur={this.handleQuestionBlur}
            onStepSubmit={this.handleStepSubmit}
            goToNextInput={this.goToNextInput}
            injectNewQuestions={this.injectNewQuestions}
            removeNewQuestions={this.removeNewQuestions}
            handleFormAnswered={this.handleFormAnswered}
            isFinished={isFinished}
          />
        </FlowContent>
      </StyledRetirementFlow>
    );
  }
}

RetirementFlow.propTypes = {
  onStepSubmit: PropTypes.func,
  formatValues: PropTypes.func,
  flowTitle: PropTypes.string,
};

RetirementFlow.defaultProps = {
  onStepSubmit: () => {},
  formatValues: values => values,
  flowTitle: 'Untitled Flow',
};

export default RetirementFlow;
